import React, { memo, useEffect, useRef } from "react";
import { IGif } from "@/common/services/types";
import { ToggleButton } from "components/buttons";
import { TagSelector } from "components/tags-selector/TagSelector";
import { Button } from "components/buttons/Button";
import { useManageGifForm } from "./useManageGifForm";
import { GifPreferenceList } from "components/manage/GifPreferenceList";
import { GifDescriptionField } from "components/manage/GifDescriptionField";
import { ManageLinks } from "components/manage/ManageLinks";
import { ScrollSectionEnum } from "@/common/buses/popups/popup-types";
import { useUserStore } from "@/common/store/userStore";
import "@/common/styles/page.scss";
import "./ManageGifForm.scss";

interface IManageGifFormProps {
  gif: IGif;
  scrollToSection?: ScrollSectionEnum;
  getUserGifs?: () => void;
  onClosePopup: () => void;
}

export const ManageGifForm: React.FC<IManageGifFormProps> = memo(({
  gif,
  scrollToSection,
  getUserGifs,
  onClosePopup
}) => {
  const verified = useUserStore((state) => state.currentUser?.verified);
  const manageLinksRef = useRef<HTMLDivElement>(null);

  const {
    sexuality,
    description,
    privacy,
    btnState,
    isMoreThanTen,
    onPrivacyChange,
    onPreferenceChange,
    onDescriptionChange,
    onTagsChange,
    onSubmit
  } = useManageGifForm({ gif, onClosePopup, getUserGifs });

  useEffect(() => {
    if (scrollToSection === ScrollSectionEnum.MANAGE_LINKS) {
      manageLinksRef.current?.scrollIntoView();
    }
  }, [scrollToSection]);

  return (
    <form className="ManageGifForm" onSubmit={onSubmit} data-testid="manage-gif-form">
      <h1>Edit</h1>
      <div className="ManageGifForm-Poster">
        <img src={gif.urls.thumbnail} alt={`Poster for ${gif.userName} gif`} />
      </div>
      <div className="ManageGifForm-Preferences">
        <h2>Preferences</h2>
        <p className="ManageGifForm-Subtitle">Choose the main audience for this content:</p>
        <GifPreferenceList selected={sexuality} onChange={onPreferenceChange} />
      </div>
      <div className="ManageGifForm-Description">
        <h2>Description</h2>
        <GifDescriptionField
          onChange={onDescriptionChange}
          description={description}
          isVerified={!!verified}
          onVerifyClick={onClosePopup}
        />
      </div>
      <div className="ManageGifForm-Tags">
        <h2>Tags</h2>
        <h4>Choose 3-10 tags that match your video</h4>
        <TagSelector onChange={onTagsChange} defaultSelectedTags={gif.tags || []} />
        {isMoreThanTen && <p className="ManageGifForm-TagsError">Please choose a maximum of 10 tags!</p>}
      </div>
      <div className="ManageGifForm-Privacy">
        <h2>Options</h2>
        <span>Make Private</span>
        <ToggleButton checked={privacy} onChange={onPrivacyChange}/>
      </div>
      <div className="ManageGifForm-Links" ref={manageLinksRef}>
        <ManageLinks gifId={gif.id} />
      </div>
      <div className="ManageGifForm-BtnWrap">
        <Button variant="primary" size="l" type="submit" disabled={btnState} aria-label="Save">Save</Button>
        <Button variant="secondary" size="l" type="button" onClick={onClosePopup} aria-label="Cancel">Cancel</Button>
      </div>
    </form>
  );
});
